import React from "react";
import { useI18n } from "../i18n";
import { CONTACT, ANDROID_APP, IOS_APP } from "../../api";
import { Phone, Mail, MapPin, MessageCircle } from "lucide-react";

export default function CustomerFooter() {
  const { t, lang } = useI18n();
  const badgeSrc =
    lang === "es"
      ? "https://play.google.com/intl/es-419_us/badges/static/images/badges/es_badge_web_generic.png"
      : "https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png";
  // Apple's badge service localizes by URL segment.
  const appStoreBadge =
    lang === "es"
      ? "https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/es-es?size=250x83"
      : "https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83";

  return (
    <footer className="bg-[#0d0d0d] text-gray-300 mt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid grid-cols-1 md:grid-cols-4 gap-8">
        <div>
          <div className="mb-3">
            <img
              src="/dams-logo-dark.png"
              alt="DAMS Rent A Car, S.R.L."
              className="h-14 w-auto object-contain"
              draggable="false"
            />
          </div>
          <p className="text-sm text-gray-400 max-w-xs mb-5">
            Premium car rentals across the Dominican Republic and beyond. Drive freely, drive far.
          </p>
          <div data-testid="footer-app-downloads" className="flex flex-col items-start gap-2">
            <span className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">
              {t("footer.getApp")}
            </span>
            <div className="flex flex-wrap items-center gap-2">
              <a
                href={ANDROID_APP.apkUrl}
                download
                aria-label={t("footer.downloadAndroid")}
                data-testid="download-android-app-btn"
                className="inline-block transition-transform hover:scale-[1.03] focus:outline-none focus:ring-2 focus:ring-white/40 rounded"
              >
                <img
                  src={badgeSrc}
                  alt={t("footer.downloadAndroid")}
                  className="h-12 w-auto"
                  draggable="false"
                />
              </a>
              <a
                href={IOS_APP.appStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t("footer.downloadIos")}
                data-testid="download-ios-app-btn"
                className="inline-block transition-transform hover:scale-[1.03] focus:outline-none focus:ring-2 focus:ring-white/40 rounded"
              >
                <img
                  src={appStoreBadge}
                  alt={t("footer.downloadIos")}
                  className="h-10 w-auto"
                  draggable="false"
                />
              </a>
            </div>
            <span className="text-[11px] text-gray-500 mt-0.5">
              {t("footer.version")} {ANDROID_APP.version}
            </span>
          </div>
        </div>
        <div>
          <h4 className="text-white text-sm font-bold mb-3 uppercase tracking-wider">{t("nav.cars")}</h4>
          <ul className="space-y-2 text-sm">
            <li>SUV</li>
            <li>Compact</li>
            <li>Luxury</li>
            <li>Electric</li>
          </ul>
        </div>
        <div>
          <h4 className="text-white text-sm font-bold mb-3 uppercase tracking-wider">{t("nav.locations")}</h4>
          <ul className="space-y-2 text-sm">
            <li>Santo Domingo</li>
            <li>Punta Cana Airport</li>
            <li>Miami International</li>
            <li>JFK Airport New York</li>
          </ul>
        </div>
        <div>
          <h4 className="text-white text-sm font-bold mb-3 uppercase tracking-wider">{t("nav.contact")}</h4>
          <ul className="space-y-2 text-sm">
            <li>
              <a href={CONTACT.whatsappLink} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white transition-colors">
                <MessageCircle size={14} className="text-emerald-400" /> {CONTACT.whatsapp} (WhatsApp)
              </a>
            </li>
            <li>
              <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-2 hover:text-white transition-colors">
                <Mail size={14} /> {CONTACT.email}
              </a>
            </li>
            <li>
              <a href={CONTACT.phoneLink} className="flex items-center gap-2 hover:text-white transition-colors">
                <Phone size={14} /> {CONTACT.phone}
              </a>
            </li>
            <li className="flex items-center gap-2"><MapPin size={14} /> Bella Vista, Santo Domingo</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 flex flex-col md:flex-row items-center justify-between gap-2 text-xs text-gray-500">
          <p>© {new Date().getFullYear()} DAMS Car Rental. {t("footer.rights")}</p>
          <div className="flex items-center gap-5">
            <a
              href="https://rental-routes.emergent.host/api/legal/terms"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-terms-link"
              className="hover:text-white transition-colors"
            >
              {t("footer.legal")}
            </a>
            <a
              href="https://rental-routes.emergent.host/api/legal/privacy"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-privacy-link"
              className="hover:text-white transition-colors"
            >
              {t("footer.privacy")}
            </a>
            <a
              href="/contact"
              data-testid="footer-help-link"
              className="hover:text-white transition-colors"
            >
              {t("footer.help")}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
